import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useTranslation } from "react-i18next";

import ButtonCst from "@components/ButtonCst";
import { CorporateBrandAssetDto, CorporateBrandAssetRequest } from "../types";
import { InvalidBrandAssetZipError } from "../services";
import { useBrandAssetEditor } from "../hooks/useBrandAssetEditor";
import ColorsPanel from "./ColorsPanel";
import LogosPanel from "./LogosPanel";
import ParametersPanel from "./ParametersPanel";

interface ModelEditorDialogProps {
  open: boolean;
  mode: "create" | "edit";
  editingDto?: CorporateBrandAssetDto;
  corporateId: number;
  isGlobalScope: boolean;
  sourceModels: CorporateBrandAssetDto[];
  corporateName: string;
  globalCorporateName: string;
  createBrandAsset: (body: CorporateBrandAssetRequest) => Promise<unknown>;
  editBrandAsset: (
    id: number,
    body: CorporateBrandAssetRequest,
  ) => Promise<unknown>;
  onClose: () => void;
  onSaved: () => void;
}

const ModelEditorDialog: React.FC<ModelEditorDialogProps> = ({
  open,
  mode,
  editingDto,
  corporateId,
  isGlobalScope,
  sourceModels,
  corporateName,
  globalCorporateName,
  createBrandAsset,
  editBrandAsset,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(["corporate-brand-asset", "common"]);
  const [tab, setTab] = useState(0);
  const [sourceId, setSourceId] = useState<number | "">("");
  const [uploadedZipName, setUploadedZipName] = useState("");
  const zipInputRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  const editor = useBrandAssetEditor({
    corporateId,
    corporateName,
    globalCorporateName,
    createBrandAsset,
    editBrandAsset,
  });

  const {
    name,
    setName,
    content,
    originalContent,
    setColor,
    removeColor,
    setLogo,
    setLogoHeight,
    setParameter,
    loadFrom,
    loadFromZipFile,
    loadingSource,
    submitting,
    save,
    saveToGlobal,
  } = editor;

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setFeedback(null);
    if (mode === "edit" && editingDto) {
      setName(editingDto.name);
      setSourceId(editingDto.id);
      loadFrom(editingDto);
    } else {
      setName("");
      setSourceId("");
      loadFrom(null);
    }
  }, [open, mode, editingDto?.id]);

  const handlePickSource = (id: number) => {
    setSourceId(id);
    const source = sourceModels.find((m) => m.id === id);
    if (source) loadFrom(source);
  };

  const handlePickZipFile = async (file?: File) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      setFeedback({ severity: "error", message: t("editor.zipInvalidFormat") });
      return;
    }
    try {
      await loadFromZipFile(file);
      setSourceId("");
    } catch (e) {
      setFeedback({
        severity: "error",
        message:
          e instanceof InvalidBrandAssetZipError
            ? t("editor.zipMissingVariables", {
                count: e.missing.length,
                list: e.missing.join(", "),
              })
            : t("editor.zipReadError"),
      });
    }
  };

  const handleSave = async () => {
    try {
      await save(mode === "edit" ? editingDto?.id : undefined);
      onSaved();
    } catch (e) {
      setFeedback({ severity: "error", message: t("feedback.saveError") });
    }
  };

  const handleSaveToGlobal = async () => {
    try {
      await saveToGlobal();
      setFeedback({ severity: "success", message: t("feedback.globalSaved") });
    } catch (e) {
      setFeedback({ severity: "error", message: t("feedback.saveError") });
    }
  };

  const needsSource = mode === "create";
  const canSave = !!name.trim() && !loadingSource && !submitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t(mode === "edit" ? "editor.editTitle" : "editor.createTitle")}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("editor.nameLabel")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            size="small"
          />

          {needsSource && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="brand-asset-source-label">
                    {t("editor.startFrom")}
                  </InputLabel>
                  <Select
                    labelId="brand-asset-source-label"
                    label={t("editor.startFrom")}
                    value={sourceId}
                    onChange={(e) => handlePickSource(Number(e.target.value))}
                  >
                    {sourceModels.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <input
                  ref={zipInputRef}
                  type="file"
                  accept=".zip,application/zip"
                  hidden
                  onChange={(e) => {
                    handlePickZipFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="outlined"
                  size="medium"
                  disabled={loadingSource}
                  onClick={() => zipInputRef.current?.click()}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  <UploadFileIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {t("editor.uploadZip")}
                </Button>
              </Box>
            </Box>
          )}

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              "& .MuiTab-root": {
                color: "text.primary",
                opacity: 0.6,
                "&.Mui-selected": { color: "primary.main", opacity: 1 },
              },
            }}
          >
            <Tab label={t("tabs.colors")} />
            <Tab label={t("tabs.logos")} />
            <Tab label={t("tabs.parameters")} />
          </Tabs>

          {loadingSource ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {tab === 0 && (
                <ColorsPanel
                  colors={content.colors}
                  originalColors={originalContent.colors}
                  onChange={setColor}
                  onDelete={removeColor}
                />
              )}
              {tab === 1 && (
                <LogosPanel logos={content.logos} onChange={setLogo} />
              )}
              {tab === 2 && (
                <ParametersPanel
                  parameters={content.parameters}
                  onLogoHeightChange={setLogoHeight}
                  onParameterChange={setParameter}
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <ButtonCst id="brand-asset-cancel" onClick={onClose} variant="outlined">
          {t("cancel")}
        </ButtonCst>
        {!isGlobalScope && (
          <ButtonCst
            id="brand-asset-save-global"
            onClick={handleSaveToGlobal}
            variant="outlined"
            disabled={!canSave}
          >
            {t("editor.saveToGlobal")}
          </ButtonCst>
        )}
        <ButtonCst
          id="brand-asset-save"
          onClick={handleSave}
          variant="contained"
          disabled={!canSave}
        >
          {t("saveButton")}
        </ButtonCst>
      </DialogActions>

      <Snackbar
        open={!!feedback}
        autoHideDuration={feedback?.severity === "error" ? 10000 : 4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {feedback ? (
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Dialog>
  );
};

export default ModelEditorDialog;
